import { defineComponent } from 'vue';
import { QBtn, QCard, QCardActions, QCardSection, QCheckbox, QDialog, QInput } from 'quasar';
import webSocket from '@/Services/webSocketService';

export default defineComponent({
    name: 'flow-v2-ask-ai-recipe',
    components: { QBtn, QCard, QCardActions, QCardSection, QCheckbox, QDialog, QInput },
    props: {
        open: { type: Boolean, default: false },
        flowId: { type: String, default: '' },
        readOnly: { type: Boolean, default: false },
    },
    emits: ['close'],
    data() {
        return {
            title: '',
            task: '',
            busy: false,
            error: '',
            lookups: [],
            optional: [],
        };
    },
    watch: {
        open(value) {
            if (value) {
                this.title = '';
                this.task = '';
                this.error = '';
                this.lookups = [];
                this.optional = [];
                this.loadSlots();
            }
        },
    },
    methods: {
        async loadSlots() {
            if (!this.flowId)
                return;
            try {
                const raw = await webSocket.AskAiPreviewRecipeSlots(this.flowId);
                const result = typeof raw === 'string' ? JSON.parse(raw) : raw;
                if (!result?.ok || !this.open)
                    return;
                this.lookups = (result.lookups || []).map(slot => ({ ...slot, label: slot.label || '' }));
                this.optional = (result.optional || []).map(slot => ({ ...slot, checked: false }));
            }
            catch (err) {
                this.lookups = [];
                this.optional = [];
            }
        },
        slotPayload() {
            const lookups = (this.lookups || []).map(slot => ({
                instanceId: slot.instanceId,
                path: slot.path,
                label: slot.label,
                kind: 'lookup',
            }));
            const marked = (this.optional || []).filter(slot => slot.checked).map(slot => ({
                instanceId: slot.instanceId,
                path: slot.path,
                label: slot.label,
                kind: 'text',
                literal: slot.literal || slot.example,
            }));
            return JSON.stringify(lookups.concat(marked));
        },
        async save() {
            const title = (this.title || '').trim();
            const task = (this.task || '').trim();
            if (!title || !task || this.busy || this.readOnly || !this.flowId)
                return;
            this.busy = true;
            this.error = '';
            try {
                const raw = await webSocket.AskAiSaveRecipe(this.flowId, title, task, this.slotPayload());
                const result = typeof raw === 'string' ? JSON.parse(raw) : raw;
                if (!result?.ok) {
                    this.error = result?.error || 'Ask AI could not save that recipe.';
                    return;
                }
                this.$emit('close');
            }
            catch (err) {
                this.error = 'Ask AI could not save that recipe.';
            }
            finally {
                this.busy = false;
            }
        },
    },
    render() {
        return (
            <QDialog modelValue={this.open} onUpdate:modelValue={(v) => { if (!v) this.$emit('close'); }}>
                <QCard style={{ width: '480px', maxWidth: '90vw' }}>
                    <QCardSection>
                        <div class="text-subtitle1">Save recipe</div>
                        <div class="text-caption text-grey-7 q-mt-xs">Saves this method body as an Ask AI example for this channel.</div>
                    </QCardSection>
                    <QCardSection>
                        <QInput
                            outlined
                            dense
                            label="Title"
                            modelValue={this.title}
                            onUpdate:modelValue={(v) => { this.title = v; }}
                        />
                        <QInput
                            class="q-mt-sm"
                            outlined
                            dense
                            autogrow
                            type="textarea"
                            label="When to use this"
                            modelValue={this.task}
                            onUpdate:modelValue={(v) => { this.task = v; }}
                        />
                        {this.lookups.length || this.optional.length ? (
                            <div class="q-mt-md" style={{ maxHeight: '240px', overflow: 'auto' }}>
                                {this.lookups.length ? (
                                    <div>
                                        <div class="text-caption text-grey-7">People will need to choose</div>
                                        {this.lookups.map((slot, index) => (
                                            <div key={'lookup-' + index} class="q-mt-sm">
                                                <QCheckbox dense disable modelValue={true} label={slot.caption ? (slot.caption + ' — ' + (slot.example || slot.label)) : (slot.example || slot.label)} />
                                                <QInput
                                                    class="q-mt-xs"
                                                    outlined
                                                    dense
                                                    label="What to ask"
                                                    modelValue={slot.label}
                                                    onUpdate:modelValue={(v) => { slot.label = v; }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ) : null}
                                {this.optional.length ? (
                                    <div class="q-mt-sm">
                                        <div class="text-caption text-grey-7">Also mark a value from this account</div>
                                        {this.optional.map((slot, index) => (
                                            <QCheckbox
                                                key={'text-' + index}
                                                class="q-mt-xs"
                                                dense
                                                modelValue={!!slot.checked}
                                                label={(slot.caption ? slot.caption + ' — ' : '') + (slot.example || slot.label)}
                                                onUpdate:modelValue={(v) => { slot.checked = v; }}
                                            />
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                        {this.error ? <div class="text-negative q-mt-sm">{this.error}</div> : null}
                    </QCardSection>
                    <QCardActions align="right">
                        <QBtn flat no-caps label="Cancel" disable={this.busy} onClick={() => this.$emit('close')} />
                        <QBtn
                            unelevated
                            no-caps
                            color="primary"
                            label="Save"
                            disable={this.busy || this.readOnly || !(this.title || '').trim() || !(this.task || '').trim()}
                            onClick={() => this.save()}
                        />
                    </QCardActions>
                </QCard>
            </QDialog>
        );
    },
});
